<template>
  <div style="background:#0A1628;min-height:100vh;padding-top:20px;">
    <div class="container">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div><h4 style="color:#E8EDF4;font-size:22px;margin:0;font-weight:700;">Menu Management</h4><p style="color:#4FC3F7;font-size:13px;margin:4px 0 0;">Manage your POS menu items &amp; categories</p></div>
        <div>
          <button class="st-btn st-btn-outline me-2" @click="showCategoryModal=true">+ Category</button>
          <button class="st-btn st-btn-primary" @click="openAddItem">+ Menu Item</button>
        </div>
      </div>

      <!-- Category Tabs -->
      <div class="st-tabs mb-3" style="overflow-x:auto;white-space:nowrap;">
        <button class="st-tab" :class="{active:filterCat===''}" @click="filterCat=''">All <span class="st-tab-count">{{ items.length }}</span></button>
        <button class="st-tab" :class="{active:filterCat===c.id}" v-for="c in categories" :key="c.id" @click="filterCat=c.id">
          {{ c.name }} <span class="st-tab-count">{{ items.filter(i=>i.category_id==c.id).length }}</span>
        </button>
      </div>

      <!-- Search -->
      <input class="st-input mb-3" v-model="search" placeholder="Search menu items..." style="max-width:400px;" />

      <!-- Items Grid -->
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-info"></div></div>
      <div v-else class="row g-3">
        <div class="col-6 col-md-4 col-lg-3" v-for="item in filteredItems" :key="item.id">
          <div class="st-menu-card" :class="{'st-inactive':!item.is_available}" @click="openEditItem(item)">
            <div class="st-menu-price">KSh {{ fNum(item.price) }}</div>
            <div class="st-menu-name">{{ item.name }}</div>
            <div class="st-menu-cat" v-if="getCatName(item.category_id)">{{ getCatName(item.category_id) }}</div>
            <div class="st-menu-status">
              <span v-if="item.is_available" style="color:#10B981;">● Available</span>
              <span v-else style="color:#EF4444;">● Unavailable</span>
            </div>
          </div>
        </div>
        <div v-if="filteredItems.length===0" class="col-12 text-center py-5" style="color:#8BA4C4;">
          <p>No menu items found. Click "+ Menu Item" to add your first item.</p>
        </div>
      </div>

      <!-- Add/Edit Item Modal -->
      <div class="st-modal-overlay" v-if="showItemModal" @click.self="showItemModal=false">
        <div class="st-modal" style="max-width:500px;">
          <div class="st-modal-header">
            <h5>{{ editingItem ? 'Edit' : 'Add' }} Menu Item</h5>
            <button class="st-modal-close" @click="showItemModal=false">&times;</button>
          </div>
          <div class="st-modal-body">
            <label class="st-label">Item Name *</label>
            <input class="st-input mb-3" v-model="itemForm.name" placeholder="e.g. Ugali + Beef Stew" />

            <label class="st-label">Category</label>
            <select class="st-input mb-3" v-model="itemForm.category_id">
              <option value="">None</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>

            <div class="row g-2 mb-3">
              <div class="col-6">
                <label class="st-label">Price (KSh) *</label>
                <input class="st-input" v-model.number="itemForm.price" type="number" min="0" step="1" placeholder="150" />
              </div>
              <div class="col-6">
                <label class="st-label">Sort Order</label>
                <input class="st-input" v-model.number="itemForm.sort_order" type="number" min="0" placeholder="0" />
              </div>
            </div>

            <label class="st-label">Description</label>
            <textarea class="st-input mb-3" v-model="itemForm.description" rows="2" placeholder="Optional description..." style="resize:vertical;"></textarea>

            <div class="d-flex align-items-center mb-3">
              <label class="st-switch">
                <input type="checkbox" v-model="itemForm.is_available" />
                <span class="st-switch-slider"></span>
              </label>
              <span style="color:#E8EDF4;font-size:14px;margin-left:10px;">Available for sale</span>
            </div>
          </div>
          <div class="st-modal-footer">
            <button v-if="editingItem" class="st-btn" style="background:#EF4444;color:#fff;margin-right:auto;" @click="deleteItem">Delete</button>
            <button class="st-btn st-btn-outline" @click="showItemModal=false">Cancel</button>
            <button class="st-btn st-btn-primary" @click="saveItem" :disabled="saving">{{ saving ? 'Saving...' : 'Save' }}</button>
          </div>
        </div>
      </div>

      <!-- Category Modal -->
      <div class="st-modal-overlay" v-if="showCategoryModal" @click.self="showCategoryModal=false">
        <div class="st-modal" style="max-width:400px;">
          <div class="st-modal-header">
            <h5>Manage Categories</h5>
            <button class="st-modal-close" @click="showCategoryModal=false">&times;</button>
          </div>
          <div class="st-modal-body">
            <div class="d-flex mb-3">
              <input class="st-input me-2" v-model="newCatName" placeholder="New category name..." style="flex:1;" @keyup.enter="addCategory" />
              <button class="st-btn st-btn-primary" @click="addCategory" :disabled="!newCatName.trim()">Add</button>
            </div>
            <div v-for="c in categories" :key="c.id" class="d-flex align-items-center justify-content-between py-2" style="border-bottom:1px solid #1B3A6A;">
              <span style="color:#E8EDF4;">{{ c.name }}</span>
              <div>
                <span style="color:#8BA4C4;font-size:12px;margin-right:12px;">{{ items.filter(i=>i.category_id==c.id).length }} items</span>
                <button class="st-btn-icon" @click="deleteCategory(c)" title="Delete" style="color:#EF4444;font-size:16px;">✕</button>
              </div>
            </div>
            <p v-if="categories.length===0" style="color:#8BA4C4;text-align:center;padding:20px 0;">No categories yet.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, simitrack } from "@/api";
export default {
  name: 'MenuManagement',
  data() {
    return {
      items: [], categories: [], loading: true, saving: false,
      filterCat: '', search: '',
      showItemModal: false, showCategoryModal: false,
      editingItem: null, newCatName: '',
      itemForm: { name: '', category_id: '', price: '', description: '', sort_order: 0, is_available: true }
    };
  },
  computed: {
    filteredItems() {
      let list = this.items;
      if (this.filterCat) list = list.filter(i => i.category_id == this.filterCat);
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(i => i.name.toLowerCase().includes(q) || (i.description||'').toLowerCase().includes(q));
      }
      return list.sort((a,b) => (a.sort_order||0) - (b.sort_order||0));
    }
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [itemsRes, catsRes] = await Promise.all([
          this.api('getMenuItems'),
          this.api('getMenuCategories')
        ]);
        this.items = itemsRes.data || [];
        this.categories = catsRes.data || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    openAddItem() {
      this.editingItem = null;
      this.itemForm = { name: '', category_id: '', price: '', description: '', sort_order: 0, is_available: true };
      this.showItemModal = true;
    },
    openEditItem(item) {
      this.editingItem = item;
      this.itemForm = { name: item.name, category_id: item.category_id||'', price: parseFloat(item.price), description: item.description||'', sort_order: parseInt(item.sort_order)||0, is_available: !!parseInt(item.is_available) };
      this.showItemModal = true;
    },
    async saveItem() {
      if (!this.itemForm.name || !this.itemForm.price) return alert('Name and price are required');
      this.saving = true;
      try {
        const fn = this.editingItem ? 'updateMenuItem' : 'addMenuItem';
        const params = { ...this.itemForm, is_available: this.itemForm.is_available ? 1 : 0 };
        if (this.editingItem) params.menuItemId = this.editingItem.id;
        await this.api(fn, params);
        this.showItemModal = false;
        await this.load();
      } catch(e) { alert('Error: ' + e.message); }
      this.saving = false;
    },
    async deleteItem() {
      if (!confirm(`Delete "${this.editingItem.name}"?`)) return;
      await this.api('deleteMenuItem', { menuItemId: this.editingItem.id });
      this.showItemModal = false;
      await this.load();
    },
    async addCategory() {
      if (!this.newCatName.trim()) return;
      await this.api('addMenuCategory', { name: this.newCatName.trim() });
      this.newCatName = '';
      await this.load();
    },
    async deleteCategory(c) {
      if (!confirm(`Delete category "${c.name}"?`)) return;
      await this.api('deleteMenuCategory', { categoryId: c.id });
      await this.load();
    },
    getCatName(id) { return (this.categories.find(c=>c.id==id)||{}).name || ''; },
    fNum(n) { return Number(n||0).toLocaleString(); },
    async api(fn, params={}) {
      try {
        const res = await executeJson({ function: fn, ...params }, simitrack);
        return res.data;
      } catch(e) {
        return { success: false, message: e.message || 'Request failed', data: null };
      }
    }
  }
};
</script>

<style scoped>
/* Page */
.st-page-title { color:#E8EDF4; font-size:22px; margin:0; }

/* Tabs */
.st-tabs { display:flex; gap:6px; flex-wrap:wrap; }
.st-tab { padding:6px 14px; border-radius:20px; background:#0F2847; border:1px solid #1B3A6A; color:#8BA4C4; font-size:13px; cursor:pointer; transition:.2s; }
.st-tab:hover { border-color:#2E86DE; }
.st-tab.active { background:#2E86DE; color:#fff; border-color:#2E86DE; }
.st-tab-count { background:rgba(255,255,255,.15); padding:1px 6px; border-radius:10px; font-size:11px; margin-left:4px; }

/* Inputs */
.st-input { width:100%; padding:10px 14px; background:#0F2847; border:1px solid #1B3A6A; border-radius:8px; color:#E8EDF4; font-size:14px; outline:none; box-sizing:border-box; }
.st-input:focus { border-color:#2E86DE; }
.st-input::placeholder { color:#4A6A8A; }
.st-label { display:block; font-size:12px; color:#8BA4C4; margin-bottom:5px; text-transform:uppercase; letter-spacing:0.3px; }

/* Buttons */
.st-btn { padding:8px 18px; border-radius:8px; border:none; font-size:13px; font-weight:600; cursor:pointer; transition:.15s; }
.st-btn-primary { background:#2E86DE; color:#fff; }
.st-btn-primary:hover { background:#2574C4; }
.st-btn-primary:disabled { opacity:0.5; }
.st-btn-outline { background:transparent; border:1px solid #1B3A6A; color:#8BA4C4; }
.st-btn-outline:hover { border-color:#2E86DE; color:#E8EDF4; }

/* Cards */
.st-menu-card { background:#0F2847; border:1px solid #1B3A6A; border-radius:12px; padding:16px; cursor:pointer; transition:all .2s; position:relative; }
.st-menu-card:hover { border-color:#2E86DE; transform:translateY(-2px); }
.st-menu-card.st-inactive { opacity:0.5; }
.st-menu-price { font-size:22px; font-weight:800; color:#10B981; margin-bottom:6px; }
.st-menu-name { font-size:15px; font-weight:600; color:#E8EDF4; margin-bottom:4px; }
.st-menu-cat { font-size:11px; color:#4FC3F7; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:6px; }
.st-menu-status { font-size:12px; }

/* Modal */
.st-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.65); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; }
.st-modal { background:#0A1B36; border:1px solid #1B3A6A; border-radius:14px; width:100%; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,.5); }
.st-modal-header { display:flex; justify-content:space-between; align-items:center; padding:16px 20px; background:#0F2847; border-bottom:1px solid #1B3A6A; }
.st-modal-header h5 { margin:0; color:#E8EDF4; font-size:16px; font-weight:700; }
.st-modal-close { background:none; border:none; color:#8BA4C4; font-size:22px; cursor:pointer; }
.st-modal-close:hover { color:#EF4444; }
.st-modal-body { padding:20px; color:#E8EDF4; max-height:70vh; overflow-y:auto; }
.st-modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:14px 20px; border-top:1px solid #1B3A6A; }

/* Toggle */
.st-switch { position:relative; display:inline-block; width:44px; height:24px; }
.st-switch input { opacity:0; width:0; height:0; }
.st-switch-slider { position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background:#1B3A6A; border-radius:24px; transition:.3s; }
.st-switch-slider:before { content:""; position:absolute; height:18px; width:18px; left:3px; bottom:3px; background:#8BA4C4; border-radius:50%; transition:.3s; }
.st-switch input:checked + .st-switch-slider { background:#10B981; }
.st-switch input:checked + .st-switch-slider:before { transform:translateX(20px); background:#fff; }
.st-btn-icon { background:none; border:none; cursor:pointer; padding:4px 8px; }
</style>